import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import * as AssuranceService from '@/services/assuranceActionsService';
import { Plus } from 'lucide-react';
import { useCurrentUser, useTenancy } from '@/contexts/TenancyContext';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { Label } from '@/components/ui/label';
import { FrameworkCombobox, FrameworksProvider, cleanStandardName } from '@/components/ui/FrameworkCombobox';
import { MCMMappingsCard, MappingLink } from '@/components/mcm/MCMMappingsCard';
import { MCMService } from '@/services/mcmService';
import { ControlCombobox, ControlsProvider } from '@/components/ui/ControlCombobox';

interface AddAssuranceTaskModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTaskAdded: () => void;
  initialForm?: typeof defaultForm;
  onTaskSaved?: (taskData: any, taskId: string) => void;
}

const defaultForm = {
  check: '',
  framework: [], // Now always an array
  assignedTo: '',
  evidence: '',
  checkDays: '30',
  reminderDays: '7',
  meetingNeeded: 'false',
  status: 'true',
  relatedControls: [] as string[], // DED-1588: Add related controls field
  nextReviewDate: '',
  lastReviewDate: '',
};

const AddAssuranceTaskModal: React.FC<AddAssuranceTaskModalProps> = ({
  open,
  onOpenChange,
  onTaskAdded,
  initialForm,
  onTaskSaved
}) => {
  const [form, setForm] = useState(initialForm || defaultForm);
  const [loading, setLoading] = useState(false);
  const [assignees, setAssignees] = useState<any[]>([]);
  const currentUser = useCurrentUser();
  const { activeTenancy } = useTenancy();
  const [staged, setStaged] = useState<Record<string, { links: MappingLink[]; standard?: string }>>({});
  const pendingIdRef = React.useRef(`internal:assurance_draft:${Math.random().toString(36).slice(2)}`);
  const pendingRefRef = React.useRef(`TSK-DRAFT-${Math.random().toString(36).slice(2,8).toUpperCase()}`);
  const pendingEntityLink = React.useMemo<MappingLink>(() => ({
    id: pendingIdRef.current,
    ref: pendingRefRef.current,
    title: form.check || 'Assurance Task',
    domain: 'Assurance',
    relation: 'assures',
  }), [form.check]);

  // Reset form when modal opens/closes or initialForm changes
  useEffect(() => {
    if (open && initialForm) {
      setForm(initialForm);
    } else if (!open) {
      setForm(initialForm || defaultForm);
    }
  }, [open, initialForm]);

  useEffect(() => {
    // TODO: Replace with actual assignee fetch logic
    setAssignees([
      { userID: currentUser?.id || 'unknown', name: currentUser?.name || 'Current User' },
    ]);
  }, [currentUser]);

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // DED-1588: Include related controls in task data
      const controlsToSave = form.relatedControls || [];
      const taskData = {
        checkDetail: {
          check: form.check,
          // DED-1026: Store framework name in uppercase for consistent filtering
          framework: Array.isArray(form.framework) && form.framework.length > 0
            ? Object.fromEntries(form.framework.map((fw, idx) => [idx, { frameName: cleanStandardName(fw).toUpperCase(), controls: controlsToSave }]))
            : {},
          assignedTo: form.assignedTo,
          evidence: form.evidence,
          meetingNeeded: form.meetingNeeded,
          active: form.status,
          procRef: controlsToSave, // DED-1588: Save related controls to procRef
        },
        checkFrequency: {
          checkDays: form.checkDays,
          reminderDays: form.reminderDays,
          sendReminder: form.meetingNeeded,
          nextCheck: new Date(new Date().getTime() + (Number(form.checkDays) * 24 * 60 * 60 * 1000)),
        },
        relatedControls: controlsToSave, // DED-1588: Also save to top-level relatedControls
        // Add review dates as Date objects
        nextReviewDate: form.nextReviewDate ? new Date(form.nextReviewDate) : undefined,
        lastReviewDate: form.lastReviewDate ? new Date(form.lastReviewDate) : undefined,
      };

      const result = await AssuranceService.createTask(taskData);
      if (result?.id && Object.keys(staged).length > 0 && activeTenancy?.id) {
        const realLink: MappingLink = {
          id: `internal:assurance:${String(result.id)}`,
          ref: String(result.id),
          title: form.check || `Assurance ${String(result.id)}`,
          domain: 'Assurance',
          relation: 'assures',
        };
        const updates = Object.entries(staged).map(async ([controlId, payload]) => {
          const links = (payload.links || []).map((lnk) => (
            (lnk.id === pendingIdRef.current || lnk.ref === pendingRefRef.current) ? realLink : lnk
          ));
          await MCMService.bulkUpdate({
            controlIds: [String(controlId)],
            type: 'linkedComponents',
            action: 'update',
            data: { linkedComponents: links },
            standard: payload.standard,
          }, activeTenancy.id);
        });
        await Promise.allSettled(updates);
      }
      if (onTaskSaved && result?.id) onTaskSaved(taskData, result.id);
      onTaskAdded();
      onOpenChange(false);
      setForm(initialForm || defaultForm);
      setStaged({});
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card overflow-hidden">
          <div className="sticky top-0 z-20 p-4 py-6 border-b border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle>Add Assurance Task</DialogTitle>
            <DialogDescription>
              Thought of a new Assurance Task and want to add it to the library? Add it here!
            </DialogDescription>
          </div>
          <div className="flex flex-col gap-4 w-full px-4">
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="taskName">Task Name</Label>
              <Input placeholder="Task Name" value={form.check} onChange={e => handleChange('check', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 w-full" tabIndex={-1}>
              <Label htmlFor="framework">Framework</Label>
              {/* <Input placeholder="Framework" value={form.framework} onChange={e => handleChange('framework', e.target.value)} /> */}
              <FrameworksProvider>
                <FrameworkCombobox
                  selected={form.framework}
                  setSelected={v => handleChange('framework', v)}
                  placeholder="Framework"
                  className="w-full"
                />
              </FrameworksProvider>
            </div>
            {/* DED-1588: Related Controls field */}
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="relatedControls">Related Controls</Label>
              <ControlsProvider>
                <ControlCombobox
                  selected={form.relatedControls || []}
                  setSelected={v => handleChange('relatedControls', v.map(String))}
                  placeholder="Select related controls..."
                  fullWidth
                />
              </ControlsProvider>
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="assignee">Assignee</Label>
              <SingleUserCombobox
                selected={form.assignedTo}
                setSelected={v => handleChange('assignedTo', v)}
                placeholder="Assignee"
                className="w-full"
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="evidence">Evidence Type</Label>
              <Select value={form.evidence} onValueChange={v => handleChange('evidence', v)}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Evidence Type" /></SelectTrigger>
                <SelectContent className="w-full">
                  <SelectItem value="Upload Evidence">Upload Evidence</SelectItem>
                  <SelectItem value="Sight Evidence">Sight Evidence</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="checkInterval">Check Interval (days)</Label>
              <Input placeholder="Check Interval (days)" value={form.checkDays} onChange={e => handleChange('checkDays', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="reminderDays">Reminder Days</Label>
              <Input placeholder="Reminder Days" value={form.reminderDays} onChange={e => handleChange('reminderDays', e.target.value)} />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="lastReviewDate">Last Review Date</Label>
              <Input 
                type="date" 
                placeholder="Last Review Date" 
                value={form.lastReviewDate} 
                onChange={e => handleChange('lastReviewDate', e.target.value)} 
              />
            </div>
            <div className="flex flex-col gap-1 w-full">
              <Label htmlFor="nextReviewDate">Next Review Date</Label>
              <Input 
                type="date" 
                placeholder="Next Review Date" 
                value={form.nextReviewDate} 
                onChange={e => handleChange('nextReviewDate', e.target.value)} 
              />
            </div>
            <div className="flex flex-row gap-4 w-full">
              <div className="flex flex-col gap-1 w-1/2">
                <Label htmlFor="meetingNeeded">Meeting Required?</Label>
                <Select value={form.meetingNeeded} onValueChange={v => handleChange('meetingNeeded', v)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Meeting Required?" /></SelectTrigger>
                  <SelectContent className="w-full">
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1 w-1/2">
                <Label htmlFor="status">Status</Label>
                <Select value={form.status} onValueChange={v => handleChange('status', v)}>
                  <SelectTrigger className="w-full"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent className="w-full">
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full px-4">
            <Label>Mappings</Label>
            <MCMMappingsCard
              tenancyId={activeTenancy?.id}
              isReadOnly={false}
              controlIds={[]}
              persistMode="staged"
              pendingEntityLink={pendingEntityLink}
              onApplyStaged={({ controlId, links, standard }) => setStaged(prev => ({ ...prev, [controlId]: { links, standard } }))}
              collapsible
            />
          </div>
          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <Button onClick={handleSubmit} disabled={loading} className="w-full mt-2">
              <Plus className="mr-2 h-4 w-4" /> Add Task
            </Button>
          </DialogFooter>
          </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddAssuranceTaskModal;

